import clsx from "clsx";
import { useState, useEffect } from "react";
import ReactTooltip from "react-tooltip";
import { Product } from "@commercetools/platform-sdk";
import { useRouter } from "next/router";
import LinkComponent from "@Components/Link";
import {
  getItemQty,
  getPriceOrDiscounted,
  getWeightValue,
} from "@Components/cart/helpers/helpers";
import { Button } from "@Components/ui";
import { TIME_TO_REDIRECT_BACK_FROM_PDP_TO_PLP_RECIPES } from "@Constants/common";
import { STORAGE_KEYS, STORAGE_TYPES } from "@Constants/storage";
import { KIOSK_VIEW_MODE_QUERY } from "@Constants/url";
import { IngredientsListItem } from "@Types/recipes-detail-page";
import { setItem } from "@Utils/browserStorage";
import { cartUtilQtyAdd, cartUtilRemoveItem } from "@Utils/cartUtils";
import { currency } from "@Utils/index";
import { recipeItemToCTItemTransformer } from "@Utils/recipesHelpers";
import { ICartLineItem } from "@wff/interfaces";
import { cartSliceSelector } from "@wff/store/cartSlice";
import { useAppDispatch, useAppSelector } from "@wff/store/hooks";
import { localeSelector } from "@wff/store/localeSlice";
import { toggleZipCodePopUp } from "@wff/store/zipcodeSlice";
import classes from "./RecipesDetail.module.scss";

interface IngredientItemProps extends IngredientsListItem {
  addToBasketText: string;
  addedText: string;
  removeInCartText: string;
  removedText: string;
  isCanadianUser?: boolean;
  addToCartButtonClassForCA?: string;
  availability?: boolean;
  isPickupAvailable?: boolean;
  currentZipCodeAvialabilityStoreIdInStorageForCA?: string;
  currentZipCodePickupAvailabilityForCA?: string;
}

interface IngredientState extends IngredientItemProps {
  ctData: Product | null | undefined;
}
interface WithLink {
  link?: string;
  className?: string;
  children: JSX.Element;
}

const WithLink = ({ link, children, className = "" }: WithLink) =>
  link ? (
    <LinkComponent href={link}>
      <a className={className}>{children}</a>
    </LinkComponent>
  ) : (
    children
  );

// eslint-disable-next-line sonarjs/cognitive-complexity
const IngredientItem = (props: IngredientItemProps) => {
  const {
    query: { viewMode },
  } = useRouter();
  const [ingredient, setIngredient] = useState<IngredientState>();
  const link = props.isWfProduct ? props.ctProductSku : "";
  const { lineItems, cartId } = useAppSelector(cartSliceSelector);
  const currentLocale = useAppSelector(localeSelector)?.languageCode;
  const lang = currentLocale ?? "en-US";
  const itemIndex = lineItems?.findIndex(
    (item: ICartLineItem) => item.sku === props.ctProductSku
  );
  const [itemQuantity, setItemQuantity] = useState(
    getItemQty(lineItems, itemIndex)
  );
  const [isInCart, setIsInCart] = useState(false);
  const [slug, setSlug] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isEventTriggered, setIsEventTriggered] = useState(false);
  const dispatch = useAppDispatch();
  const attribs =
    ingredient?.ctData?.masterData?.current?.masterVariant?.attributes ?? [];
  const priceData =
    ingredient?.ctData?.masterData?.current?.masterVariant?.prices;
  const weight = getWeightValue(attribs);
  const totalPrice = currency.format(getPriceOrDiscounted(priceData));
  const sku = props.ctProductSku ? props.ctProductSku : "";
  const removeInCartTextWithQuantity = props.removeInCartText
    .split("#")
    .join(`${itemQuantity}`);
  const handleRemoveItemPayload = {
    lineItems: lineItems,
    sku: sku,
    dispatch: dispatch,
  };
  const {
    isCanadianUser,
    currentZipCodeAvialabilityStoreIdInStorageForCA,
    addToCartButtonClassForCA,
    availability,
    isPickupAvailable,
    currentZipCodePickupAvailabilityForCA,
  } = props;

  const incrementalQtyPayload = {
    itemIndex: itemIndex,
    itemQuantity: itemQuantity,
    availability: 999,
    sku: sku,
    callback: () => {
      setItemQuantity(1);
      setIsLoading(false);
    },
    dispatch: dispatch,
  };
  const tooltipPosition = props.title.length * -3.5;
  const itemInCartClass = isInCart ? classes["is_in_cart"] : "";
  const dynamicBtnClass = isInCart ? classes["is_in_cart_btn"] : "";
  const isKioskViewMode = viewMode === KIOSK_VIEW_MODE_QUERY;
  const router = useRouter();
  useEffect(() => {
    if (ingredient?.ctData) return;
    const getData = async (props: IngredientItemProps) => {
      return await recipeItemToCTItemTransformer(props).then((res) => {
        setIngredient({ ...props, ctData: res }),
          setSlug(
            res?.masterData?.current?.slug[lang]
              ? res?.masterData?.current?.slug[lang]
              : "#"
          );
      });
    };

    getData(props);
  }, [props, lang, ingredient?.ctData]);

  useEffect(() => {
    const newQty = getItemQty(lineItems, itemIndex);
    if (newQty && newQty > 0) {
      setIsInCart(true);
      setItemQuantity(newQty);
    } else {
      setIsInCart(false);
      setIsLoading(false);
    }
  }, [lineItems, itemIndex]);

  const handleAddToCart = () => {
    ReactTooltip.hide();
    setIsLoading(true);
    if (!isCanadianUser) {
      cartUtilQtyAdd(cartId, incrementalQtyPayload);
    } else {
      if (
        (currentZipCodeAvialabilityStoreIdInStorageForCA?.length === 0 &&
          currentZipCodePickupAvailabilityForCA?.length === 0) ||
        (!availability && !isPickupAvailable) ||
        (currentZipCodeAvialabilityStoreIdInStorageForCA === "false" &&
          currentZipCodePickupAvailabilityForCA === "false")
      ) {
        setItem(
          STORAGE_KEYS.INCREMENT_PAYLOAD_CA,
          JSON.stringify(incrementalQtyPayload),
          STORAGE_TYPES.LOCAL
        );
        dispatch(toggleZipCodePopUp());
      } else {
        cartUtilQtyAdd(cartId, incrementalQtyPayload);
      }
    }
  };

  useEffect(() => {
    document?.addEventListener("touchstart", () => setIsEventTriggered(true));
    document?.addEventListener("touchend", () => setIsEventTriggered(false));

    let timeoutId: NodeJS.Timeout;
    if (!isEventTriggered && isCanadianUser && isKioskViewMode) {
      timeoutId = setTimeout(() => {
        router?.back();
      }, TIME_TO_REDIRECT_BACK_FROM_PDP_TO_PLP_RECIPES);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      document.removeEventListener("touchstart", () =>
        setIsEventTriggered(false)
      );
      document.removeEventListener("touchend", () =>
        setIsEventTriggered(false)
      );
    };
  }, [isEventTriggered, isCanadianUser, isKioskViewMode, router]);

  return (
    <li
      className={clsx(
        classes["ingredient-item"],
        isKioskViewMode && classes.kiosk
      )}
    >
      <WithLink
        link={isKioskViewMode ? undefined : `/${lang}/products/${slug}`}
      >
        <>
          <p className={itemInCartClass}>{props.title}</p>
          <p className={itemInCartClass}>{props.ingredientQuantity}</p>
        </>
      </WithLink>

      <div className={classes["button-wrapper"]}>
        {link && !isInCart && (
          <>
            {!isKioskViewMode && (
              <Button
                className={
                  !isCanadianUser ? dynamicBtnClass : addToCartButtonClassForCA
                }
                data-tip
                data-for={`registerTip-${props.title}`}
                disabled={!isCanadianUser ? isLoading : false}
                onClick={handleAddToCart}
              >
                {isLoading ? props.addedText : props.addToBasketText}
              </Button>
            )}

            <ReactTooltip
              key={props.title}
              className={classes["tooltip"]}
              id={`registerTip-${props.title}`}
              place="top"
              type="dark"
              effect="solid"
              offset={{ top: 0, left: tooltipPosition }}
              arrowColor="#3d3933"
            >
              {`${props.title} - ${weight} • ${totalPrice}`}
            </ReactTooltip>
          </>
        )}

        {isInCart && (
          <>
            <Button
              className={dynamicBtnClass}
              data-tip
              data-for={`registerTip-${props.title}`}
              onClick={() => {
                setIsLoading(true);
                cartUtilRemoveItem(handleRemoveItemPayload);
                setIsEventTriggered(true);
              }}
              disabled={!isCanadianUser ? isLoading : false}
            >
              {isLoading ? props.removedText : removeInCartTextWithQuantity}
            </Button>
          </>
        )}
      </div>
    </li>
  );
};

export default IngredientItem;
